import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { Logger } from 'src/utils/Logger';
import { ApiError } from 'src/error/ApiError';
import useUnshareTenantSkill from './hooks/useUnshareTenantSkill';

type UnshareSkillButtonProps = {
  skillId: string;
  skillLabel: string;
  /** Set by staff acting on a company that is not their own. It says "remove from the library",
   * since they are not that company unsharing, and goes through the staff route. */
  companyId?: string;
};

export default function UnshareSkillButton({
  skillId,
  skillLabel,
  companyId,
}: UnshareSkillButtonProps) {
  const isStaffRemoval = companyId !== undefined;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutateAsync, isPending } = useUnshareTenantSkill(companyId);

  const closeConfirm = () => {
    setErrorMessage(null);
    setIsConfirmOpen(false);
  };

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      await mutateAsync(skillId);
      setIsConfirmOpen(false);
      notifications.show({
        message: isStaffRemoval
          ? `Removed '${skillLabel}' from the library`
          : `Unshared '${skillLabel}'`,
        color: 'green',
      });
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'An error occurred';
      // A 404 means the entry is already gone, which staff removal makes an expected outcome, so it
      // is a warning rather than a fault worth an exception in Sentry. The refetch that follows it
      // flips the row to Share and unmounts this button, so the message has to go in a toast.
      if (error instanceof ApiError && error.status === 404) {
        Logger.warn(`Could not unshare skill: ${message}`);
        setIsConfirmOpen(false);
        notifications.show({ message, color: 'yellow' });
      } else {
        Logger.error(`Could not unshare skill: ${message}`, error);
        setErrorMessage(message);
      }
    }
  };

  return (
    <>
      <Button
        variant="default"
        size="compact-sm"
        aria-label={isStaffRemoval ? `Remove ${skillLabel} from library` : `Unshare ${skillLabel}`}
        onClick={() => {
          setErrorMessage(null);
          setIsConfirmOpen(true);
        }}
      >
        {isStaffRemoval ? 'Remove from library' : 'Unshare'}
      </Button>
      <ConfirmDeleteModal
        opened={isConfirmOpen}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        title={
          isStaffRemoval ? `Remove '${skillLabel}' from the library?` : `Unshare '${skillLabel}'?`
        }
        message={
          isStaffRemoval
            ? 'Removing it from the library means no other firm can add it. Firms that have already added it keep their own copy.'
            : 'Unsharing removes it from the library so no other firm can add it. Firms that have already added it keep their own copy.'
        }
        confirmLabel={isStaffRemoval ? 'Remove' : 'Unshare'}
        error={errorMessage ? { message: errorMessage } : null}
        isLoading={isPending}
      />
    </>
  );
}
